/* eslint @typescript-eslint/ban-types: 0 */
import { validate as uuidValidate } from 'uuid';
import {
    IAnyType,
    types,
    IType,
    SnapshotOut,
    getSnapshot,
    SnapshotOrInstance,
    ISimpleType,
    IMaybe,
    cast,
    Instance,
    SnapshotIn,
    IAnyModelType,
    isStateTreeNode,
} from 'mobx-state-tree';
import { DateTime } from 'luxon';
import { Stringifiable } from './utilityTypes';
import { IKeyValueMap, values } from 'mobx';
import {
    isValidCEP,
    isValidCNPJ,
    isValidCPF,
} from '@brazilian-utils/brazilian-utils';
import { STNValue } from 'mobx-state-tree/dist/internal';

type ValidationFn<T> = (value: string) => T;

const CustomDateType = (
    name: string,
    fromSnapshot: ValidationFn<DateTime>,
    toSnapshot: (value: DateTime) => string
) => {
    return types.custom<string, DateTime>({
        name,
        fromSnapshot,
        toSnapshot,
        isTargetType(value: string | DateTime): boolean {
            return value instanceof DateTime;
        },

        getValidationMessage(value: string) {
            return fromSnapshot(value).isValid
                ? ''
                : `Invalid date supplied to ${name}`;
        },
    });
};

const SQLDateTime = CustomDateType(
    'SQLDateTime',
    v => DateTime.fromSQL(v, { setZone: true }),
    v => v.toSQL()
);

const SQLDate = CustomDateType(
    'SQLDate',
    v => DateTime.fromSQL(v, { zone: 'UTC' }),
    v => v.toSQLDate()
);

const ISODateTime = CustomDateType(
    'ISODateTime',
    v => DateTime.fromISO(v, { setZone: true }),
    v => v.toISO()
);

const ISODate = CustomDateType(
    'ISODate',
    v => DateTime.fromISO(v, { zone: 'UTC' }),
    v => v.toISODate()
);

const validateUuid = (s: string): boolean => uuidValidate(s);

const UUID = types.custom<string, string>({
    name: 'UUID',
    fromSnapshot(snapshot: string) {
        return snapshot;
    },
    toSnapshot(value: string) {
        return value;
    },
    isTargetType(value: string): value is string {
        return validateUuid(value);
    },
    getValidationMessage(snapshot: string) {
        return validateUuid(snapshot) ? '' : 'This value is not an UUID v4';
    },
});

const CPF = types.refinement(types.string, s => isValidCPF(s));

const CNPJ = types.refinement(types.string, s => isValidCNPJ(s));

const CEP = types.refinement(types.string, s => isValidCEP(s));

const CPFCNPJ = types.refinement(
    types.string,
    s => isValidCPF(s) || isValidCNPJ(s)
);

const TypedIdentifier = <T extends IAnyType>(type: T) => {
    return types.refinement(types.identifier, x => type.is(x));
};

const TypedIdentifierNumber = <T extends IAnyType>(type: T) => {
    return types.refinement(types.identifierNumber, x => type.is(x));
};

const ReadonlyEnum = <T extends string>(values: readonly T[]) =>
    types.enumeration(Array.from(values));

const NonNegativeInteger = types.refinement(types.integer, x => x >= 0);

const NonNegativeMaybe: IMaybe<ISimpleType<number>> = types.snapshotProcessor(
    types.maybe(types.integer),
    {
        preProcessor(value?: number) {
            return value !== undefined && value !== null && value >= 0
                ? value
                : undefined;
        },
    }
);

const FloatCurrency = types.snapshotProcessor(types.integer, {
    preProcessor(value: number) {
        return Math.round(value * 100);
    },

    postProcessor(value: number) {
        return value / 100;
    },
});

const Cents = types.integer;

const NonNegativeCents = NonNegativeInteger;

const StringOfLength = (length: number): ISimpleType<string> =>
    types.refinement(types.string, string => string.length === length);

const WeakInteger = types.snapshotProcessor(types.maybeNull(types.integer), {
    preProcessor(value: number | string | null) {
        if (value === null) return value;
        return typeof value === 'number' ? value : parseInt(value);
    },
});

const WeakBoolean = types.snapshotProcessor(types.maybeNull(types.boolean), {
    preProcessor(value: boolean | string) {
        return value === null ||
            value === undefined ||
            typeof value === 'boolean'
            ? value
            : value.toLowerCase() === 'true';
    },
});

const StringOfRange = ({
    min,
    max,
}: {
    min?: number;
    max?: number;
}): ISimpleType<string> =>
    types.refinement(
        types.string,
        string =>
            (!min || string.length >= min) && (!max || string.length <= max)
    );

/* eslint @typescript-eslint/explicit-module-boundary-types: 0 */
const Format = <C, S, T>(type: IType<C, S, T>, toString: (s: T) => string) => {
    const modelType = types
        .model('', {
            value: type,
        })
        .views(self => ({
            get asString() {
                return toString(self.value);
            },
        }));

    return types.snapshotProcessor(modelType, {
        preProcessor(x: C) {
            return {
                value: x,
            };
        },
        postProcessor(snapshot) {
            return snapshot.value;
        },
    });
};

/**
 * @param defaultValue Required if model needs a snapshot to be instantiated
 */
const AutoInit = <T extends IAnyType, C extends SnapshotOut<T>>(
    type: T,
    defaultValue?: C,
    env?: unknown
) => types.optional(type, () => type.create(defaultValue, env));

const Filter = <T extends IAnyType>(ValueType: T, name: string) => {
    const FilterItem = types.model(`${name}Item`, {
        label: types.string,
        value: ValueType,
    });
    const Options = types.array(FilterItem);
    const Selected = types.array(ValueType);
    const FilterModel = types
        .model({
            options: Options,
            selected: Selected,
        })
        .actions(self => ({
            setSelected(selected: SnapshotOrInstance<T>[]) {
                self.selected.replace(
                    selected.filter(selected =>
                        self.options.some(option => option.value === selected)
                    )
                );
            },
        }))
        .actions(self => ({
            update(options: SnapshotOrInstance<typeof FilterItem>[]) {
                self.options.replace(options.map(option => cast(option)));
                self.setSelected(self.selected);
            },
            reset() {
                self.selected.clear();
            },
        }))
        .views(self => ({
            get selectedValues() {
                return getSnapshot(self.selected);
            },

            get selectedLabels() {
                return self.options
                    .filter(o => self.selected.includes(o.value))
                    .map(o => o.label);
            },
        }));
    return (snapshot?: SnapshotOut<typeof FilterModel>) =>
        FilterModel.named(name).props({
            options: AutoInit(Options, snapshot?.options),
            selected: AutoInit(Selected, snapshot?.selected),
        });
};

const SearchFilter = <T extends string>(values: readonly T[], name: string) => {
    const ValueType = ReadonlyEnum(values);
    const SearchFilterItem = types.model(`${name}Item`, {
        label: types.string,
        value: ValueType,
    });
    const Options = types.array(SearchFilterItem);
    const SearchFilterModel = types
        .model({
            options: Options,
            searchType: ValueType,
            stringSearch: types.string,
        })
        .actions(self => ({
            setStringSearch(stringSearch: string) {
                self.stringSearch = stringSearch;
            },
            setSearchType(searchType: STNValue<T, ISimpleType<T>>) {
                self.searchType = searchType;
            },
        }));
    return (snapshot?: SnapshotOut<typeof SearchFilterModel>) =>
        SearchFilterModel.named(name).props({
            options: AutoInit(Options, snapshot?.options),
            searchType: AutoInit(ValueType, snapshot?.searchType || values[0]),
            stringSearch: AutoInit(types.string, ''),
        });
};

const OrderFilter = <T extends string>(values: readonly T[], name: string) => {
    const ValueType = ReadonlyEnum(values);
    const OrderFilterItem = types.model(`${name}Item`, {
        label: types.string,
        value: ValueType,
    });
    const Options = types.array(OrderFilterItem);
    const OrderFilterModel = types
        .model({
            options: Options,
            orderType: ValueType,
        })
        .actions(self => ({
            setOrderType(orderType: STNValue<T, ISimpleType<T>>) {
                self.orderType = orderType;
            },
        }));
    return (snapshot?: SnapshotOut<typeof OrderFilterModel>) =>
        OrderFilterModel.named(name).props({
            options: AutoInit(Options, snapshot?.options),
            orderType: AutoInit(ValueType, snapshot?.orderType || values[0]),
        });
};

const MergeMap = <IT extends IAnyType>(Model: IT) => {
    // in case the model is a types.late and references the parent
    const idKey = Model.identifierAttribute ?? 'id';
    const BaseType = types
        .model('SmartMap', {
            inner: types.map(Model),
        })
        .views(self => ({
            get array() {
                return values(self.inner) as Instance<IT>[];
            },
        }))
        .actions(self => ({
            get<K extends Stringifiable>(key: K) {
                return self.inner.get(key.toString());
            },
        }))
        .actions(self => ({
            set<K extends Stringifiable>(key: K, snapshot: SnapshotIn<IT>) {
                const current = self.get(key);
                const currentSnapshot: SnapshotOut<IT> | undefined =
                    current && getSnapshot(current);
                const merged = currentSnapshot
                    ? { ...currentSnapshot, ...snapshot }
                    : snapshot;
                return self.inner.set(key.toString(), merged);
            },
        }))
        .actions(self => ({
            put(snapshot: SnapshotIn<IT>) {
                const id = snapshot[idKey];
                self.set(id, snapshot);
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return self.get(id)!;
            },
        }));

    const ProcessedType = types.snapshotProcessor(BaseType, {
        preProcessor(snapshot: SnapshotIn<IKeyValueMap<IT>>) {
            return { inner: snapshot };
        },

        postProcessor({ inner }) {
            return inner;
        },
    });
    return ProcessedType;
};

const DiscriminatedUnion = <L extends string, S extends {}>(
    discMap: Record<L, IType<S, S, S>>,
    discriminatorAccessor: (x: S) => L,
    name = 'DiscriminatedUnion'
) => {
    type I = Instance<IType<S, S, S & Record<string, unknown>>>;
    const mapToString = `{${Object.entries(discMap)
        .map(([k, m]) => `${k}: ${(m as IAnyModelType).name}`)
        .join(', ')}}`;
    const safeAccessor = (s?: S) => {
        if (s) {
            const key = discriminatorAccessor(s);
            const model = key && discMap[key];
            if (model) return model;
            return (
                `Discriminator with key '${key}' could not be resolved on map` +
                `${mapToString}`
            );
        }
        return `Cannot resolve '${s}' to any key on ${mapToString}`;
    };
    return types.custom<S, I>({
        name,
        fromSnapshot(s) {
            const model = safeAccessor(s);
            if (typeof model !== 'string') {
                return model.create(s);
            }
            throw new TypeError(model);
        },
        toSnapshot(t) {
            if (isStateTreeNode(t)) {
                return getSnapshot(t);
            }
            throw new TypeError(`Couldn't get snapshot of ${t}`);
        },
        isTargetType(snapOrInstance) {
            return isStateTreeNode(snapOrInstance);
        },
        getValidationMessage(s) {
            const model = safeAccessor(s);
            return typeof model === 'string' ? model : '';
        },
    });
};

function TablePage<T extends IAnyType>(type: T) {
    return types
        .model(`TablePage-${type.name}`, {
            id: types.identifierNumber,
            entries: types.array(
                types.safeReference(type, { acceptsUndefined: false })
            ),
            after: types.maybeNull(types.string),
            dirty: AutoInit(types.boolean, false),
        })
        .actions(self => ({
            setDirty() {
                self.dirty = true;
            },
        }));
}

class _TablePageWrapper<T extends IAnyType> {
    inferred(e: T) {
        return TablePage<T>(e);
    }
}

type TablePageInstance<T extends IAnyType> = Instance<
    ReturnType<_TablePageWrapper<T>['inferred']>
>;

export {
    AutoInit,
    Cents,
    CEP,
    CNPJ,
    CPF,
    CPFCNPJ,
    DiscriminatedUnion,
    Filter,
    FloatCurrency,
    Format,
    ISODate,
    ISODateTime,
    MergeMap,
    NonNegativeCents,
    NonNegativeInteger,
    NonNegativeMaybe,
    OrderFilter,
    ReadonlyEnum,
    SearchFilter,
    SQLDate,
    SQLDateTime,
    StringOfLength,
    StringOfRange,
    TablePage,
    TypedIdentifier,
    TypedIdentifierNumber,
    UUID,
    WeakBoolean,
    WeakInteger,
};
export type { TablePageInstance };
